import React, { useState } from "react";
import { Mountains, CaretDown, ArrowSquareOut, ArrowsVertical } from "@phosphor-icons/react";
import { relTime } from "../lib/incidents";
import { usePersistentState } from "../lib/usePersistentState";

function PassRow({ pass }) {
  const [open, setOpen] = useState(false);
  const hasExtra = !!(
    pass.road_condition || pass.restriction_north || pass.restriction_south ||
    pass.travel_advisory || pass.last_updated || pass.weather
  );
  const mapsUrl = pass.latitude && pass.longitude
    ? `https://www.google.com/maps?q=${pass.latitude},${pass.longitude}`
    : null;

  // Pull short condition summary (first sentence)
  const shortCond = pass.road_condition ? pass.road_condition.split(".")[0] : null;

  return (
    <li data-testid={`pass-${pass.id}`} className="hover:bg-tac-surface/60 transition-colors">
      <button
        data-testid={`pass-toggle-${pass.id}`}
        onClick={() => hasExtra && setOpen((v) => !v)}
        className={`w-full text-left px-4 py-2 flex items-center justify-between gap-2 ${hasExtra ? "cursor-pointer" : "cursor-default"}`}
      >
        <div className="min-w-0 flex-1">
          <div className="text-xs text-tac-text font-medium leading-snug truncate">
            {pass.name}
          </div>
          <div className="text-[15px] font-mono-tac text-tac-faint flex items-center gap-2 flex-wrap">
            <span>{pass.elevation_ft != null ? `${pass.elevation_ft.toLocaleString()} ft` : "—"}</span>
            {pass.temperature_f != null && <span>· {pass.temperature_f}°F</span>}
            {pass.weather && <span className="truncate max-w-[120px]">· {pass.weather}</span>}
          </div>
        </div>
        <div className="text-right flex items-center gap-1.5">
          <div className="text-[15px] font-mono-tac uppercase tracking-wider text-tac-dim line-clamp-1 max-w-[180px]">
            {shortCond}
          </div>
          {hasExtra && (
            <CaretDown size={10} weight="bold" className={`text-tac-faint transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
          )}
        </div>
      </button>

      {open && hasExtra && (
        <div data-testid={`pass-detail-${pass.id}`} className="px-4 pb-3 space-y-2 text-[11px]">
          {pass.road_condition && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-tac-faint mb-0.5">Road Condition</div>
              <p className="text-tac-text leading-relaxed whitespace-pre-line">{pass.road_condition}</p>
            </div>
          )}
          {pass.weather && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-tac-faint mb-0.5">Weather</div>
              <p className="text-tac-dim leading-relaxed">{pass.weather}</p>
            </div>
          )}
          {(pass.restriction_north || pass.restriction_south) && (
            <div className="grid grid-cols-2 gap-2 pt-1">
              {pass.restriction_north && (
                <div className="border border-tac-border bg-tac-bg/40 rounded-sm p-2">
                  <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-tac-faint mb-1">
                    <ArrowsVertical size={9} weight="bold" /> Northbound
                  </div>
                  <p className="text-tac-text leading-snug">{pass.restriction_north}</p>
                </div>
              )}
              {pass.restriction_south && (
                <div className="border border-tac-border bg-tac-bg/40 rounded-sm p-2">
                  <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-tac-faint mb-1">
                    <ArrowsVertical size={9} weight="bold" className="rotate-180" /> Southbound
                  </div>
                  <p className="text-tac-text leading-snug">{pass.restriction_south}</p>
                </div>
              )}
            </div>
          )}
          {pass.travel_advisory && (
            <div className="border-l-2 border-tac-warning pl-2 text-tac-warning">
              <span className="text-[9px] uppercase tracking-wider font-bold">Travel Advisory: </span>
              <span className="text-tac-text">{pass.travel_advisory}</span>
            </div>
          )}
          <div className="flex items-center justify-between gap-2 pt-1">
            <span className="text-[15px] font-mono-tac text-tac-faint">
              {pass.last_updated ? `Updated ${relTime(pass.last_updated)}` : ""}
            </span>
            {mapsUrl && (
              <a
                data-testid={`pass-map-link-${pass.id}`}
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-tac-primary hover:underline"
              >
                <ArrowSquareOut size={10} weight="bold" /> Open in Maps
              </a>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

export default function MountainPassPanel({ passes }) {
  const [collapsed, setCollapsed] = usePersistentState("okwatch:panel:passes:collapsed", false);
  const [showAll, setShowAll] = useState(false);
  if (!passes || passes.length === 0) return null;

  const top = passes.slice(0, showAll ? passes.length : 3);

  return (
    <div data-testid="mountain-pass-panel" className="border-b border-tac-border bg-tac-surface/40">
      <div className="px-4 py-2.5 flex items-center justify-between gap-2 border-b border-tac-border/60">
        <button
          data-testid="mountain-pass-header"
          onClick={() => setCollapsed((v) => !v)}
          className="flex items-center gap-2 group flex-1 min-w-0 text-left"
        >
          <CaretDown
            size={11}
            weight="bold"
            className={`text-tac-faint group-hover:text-tac-text transition-transform shrink-0 ${collapsed ? "-rotate-90" : ""}`}
          />
          <Mountains size={14} weight="duotone" className="text-tac-info shrink-0" />
          <span className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint group-hover:text-tac-text">
            Mountain Passes
          </span>
          <span className="text-[15px] font-mono-tac tabular text-tac-dim">· {passes.length}</span>
        </button>
        {!collapsed && passes.length > 3 && (
          <button
            data-testid="mountain-pass-toggle"
            onClick={() => setShowAll(!showAll)}
            className="text-[15px] font-bold uppercase tracking-wider text-tac-dim hover:text-tac-text flex items-center gap-1 shrink-0"
          >
            {showAll ? "Show less" : `+${passes.length - 3} more`}
            <CaretDown size={10} className={showAll ? "rotate-180" : ""} />
          </button>
        )}
      </div>
      {!collapsed && (
        <ul className="divide-y divide-tac-border/60">
          {top.map((p) => <PassRow key={p.id} pass={p} />)}
        </ul>
      )}
    </div>
  );
}
